import api from './api';

const isIndependent = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return user.type === 'independent_agent';
};

// The independent-agent lead endpoints (backend/routes/independent/leads.js)
// return raw rows/objects (no {success, data} envelope, no server-side
// pagination) since they were built as a standalone one-off before this
// unification - normalized here so LeadsManagement.jsx can treat agency and
// independent agents identically without branching.
const normalizeList = (rows, filters = {}) => {
  let data = Array.isArray(rows) ? rows : [];
  if (filters.status) data = data.filter(l => l.status === filters.status);
  if (filters.source) data = data.filter(l => l.source === filters.source);
  return {
    success: true,
    data,
    pagination: { total: data.length, pages: 1, page: 1, limit: data.length || 20 }
  };
};

export const leadsService = {
  // Get all leads with optional filters
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();

    if (!isIndependent()) {
      if (filters.status) params.append('status', filters.status);
      if (filters.source) params.append('source', filters.source);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
    }

    const endpoint = isIndependent() ? '/independent/leads' : '/leads';
    const response = await api.get(`${endpoint}?${params.toString()}`);

    return isIndependent() ? normalizeList(response.data, filters) : response.data;
  },

  // Get single lead by ID
  getById: async (id) => {
    const endpoint = isIndependent() ? `/independent/leads/${id}` : `/leads/${id}`;
    const response = await api.get(endpoint);
    return isIndependent() ? { success: true, data: response.data } : response.data;
  },

  // Create a lead manually
  create: async (leadData) => {
    if (isIndependent()) {
      const response = await api.post('/independent/leads', {
        first_name: leadData.first_name,
        last_name: leadData.last_name,
        email: leadData.email,
        phone: leadData.phone,
        destination: leadData.destination,
        travel_dates: leadData.travel_dates,
        budget_range: leadData.budget_range,
        party_size: leadData.party_size,
        message: leadData.message,
        source: leadData.source || 'manual',
        priority: leadData.priority || 'medium'
      });
      return { success: true, data: response.data };
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const response = await api.post('/leads', {
      agent_id: user.id,
      first_name: leadData.first_name,
      last_name: leadData.last_name,
      email: leadData.email,
      phone: leadData.phone,
      source: leadData.source || 'manual',
      travel_dates: { raw: leadData.travel_dates || '' },
      adults: leadData.party_size || 1,
      children: 0,
      additional_details: [leadData.destination, leadData.budget_range, leadData.message]
        .filter(Boolean).join(' \u2013 ')
    });
    return response.data;
  },

  // Delete a lead
  delete: async (id) => {
    const endpoint = isIndependent() ? `/independent/leads/${id}` : `/leads/${id}`;
    const response = await api.delete(endpoint);
    return isIndependent() ? { success: true, ...response.data } : response.data;
  },

  // Update lead status (and optionally priority)
  updateStatus: async (id, statusData) => {
    const endpoint = isIndependent() ? `/independent/leads/${id}` : `/leads/${id}/status`;
    const response = await api.put(endpoint, statusData);
    return isIndependent() ? { success: true, data: response.data } : response.data;
  },

  // Convert lead to quote
  convertToQuote: async (id) => {
    const endpoint = isIndependent() ? `/independent/leads/${id}/convert` : `/leads/${id}/convert`;
    const response = await api.post(endpoint);
    return response.data;
  },

  // Get lead statistics (for dashboard widgets) - agency only for now
  getStats: async () => {
    const response = await api.get('/leads/stats');
    return response.data;
  },

  isIndependent
};

export default leadsService;
